import { useMutation, useQuery } from '@tanstack/react-query';
import { showToast } from 'app/shared/util/toast.util';
import { queryClient } from 'app/app';
import {
  CasePublicAuthorityModel,
  SpecialRegulationsComplianceModel,
} from 'app/edozvola/cases/special-regulations-compliance/model/special-regulations-compliance.model';
import {
  copyCasePublicAuthorities,
  deleteCasePublicAuthority,
  fetchCasePublicAuthorities,
  getCasePublicAuthority,
  getSpecialRegulationsCompliance,
  moveCasePublicAuthorityOrderingDown,
  moveCasePublicAuthorityOrderingUp,
  saveCasePublicAuthority,
  saveSpecialRegulationsCompliance
} from 'app/edozvola/cases/special-regulations-compliance/service/special-regulations-compliance.service';
import { toast } from 'react-toastify';
import { PaginationState } from 'app/shared/pagination/pagination.model';
import success = toast.success;
import error = toast.error;
import {messages, TABLE_QUERY_KEYS} from "app/config/constants";

export const saveCasePublicAuthorityQuery = (idCase: string, onSuccessCallback) => {
  return useMutation({
    mutationFn: (casePublicAuthority: CasePublicAuthorityModel) => {
      return saveCasePublicAuthority(idCase, casePublicAuthority);
    },
    onSuccess: () => {
      showToast(success, messages.DATA_SAVED);
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.CASE_PUBLIC_AUTHORITY_TABLE] });
      onSuccessCallback();
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const fetchCasePublicAuthoritiesQuery = (pagination: PaginationState, idCase: string) => {
  return useQuery({
    queryKey: [TABLE_QUERY_KEYS.CASE_PUBLIC_AUTHORITY_TABLE, { idCase: idCase, pagination: { ...pagination } }],
    queryFn: ({ signal }) => fetchCasePublicAuthorities(idCase, pagination.page, pagination.size, pagination.column, pagination.direction),
  });
};

export const fetchCasePublicAuthorityQuery = (idCasePublicAuthority: string) => {
  return useQuery({
    queryKey: ['case-public-authority', idCasePublicAuthority],
    queryFn: () => getCasePublicAuthority(idCasePublicAuthority),
    enabled: !!idCasePublicAuthority,
  });
};

export const deleteCasePublicAuthorityQuery = () => {
  return useMutation({
    mutationFn: (idCasePublicAuthority: number) => deleteCasePublicAuthority(idCasePublicAuthority),
    onSuccess: () => {
      showToast(success, messages.DATA_DELETED);
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.CASE_PUBLIC_AUTHORITY_TABLE] });
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const moveCasePublicAuthorityUpQuery = () => {
  return useMutation({
    mutationFn: (idCasePublicAuthority: number) => moveCasePublicAuthorityOrderingUp(idCasePublicAuthority),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.CASE_PUBLIC_AUTHORITY_TABLE] });
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const moveCasePublicAuthorityDownQuery = () => {
  return useMutation({
    mutationFn: (idCasePublicAuthority: number) => moveCasePublicAuthorityOrderingDown(idCasePublicAuthority),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.CASE_PUBLIC_AUTHORITY_TABLE] });
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const saveSpecialRegulationsComplianceQuery = (idCase: string, resetFunction) => {
  return useMutation({
    mutationFn: (specialRegulationsCompliance: SpecialRegulationsComplianceModel) => saveSpecialRegulationsCompliance(idCase, specialRegulationsCompliance),
    onSuccess: (data, variables, context) => {
      showToast(success, messages.DATA_SAVED);
      resetFunction({ values: variables });
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const fetchSpecialRegulationsComplianceQuery = (idCase: string) => {
  return useQuery({
    queryKey: ['special-regulations-compliance', { idCase: idCase }],
    queryFn: ({ signal }) => getSpecialRegulationsCompliance(idCase),
  });
};

export const copyPublicAuthoritiesFromCaseQuery = (caseUuid: string, onSuccessCallback: any) => {
  return useMutation({
    mutationFn: (caseUuidToCopyFrom: string) => copyCasePublicAuthorities(caseUuid, caseUuidToCopyFrom),
    onSuccess: () => {
      onSuccessCallback();
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.CASE_PUBLIC_AUTHORITY_TABLE] });
      showToast(success, messages.DATA_COPIED);
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

